/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import util from '../../../../share/util/utils';
import { apiErrorDefaultAction } from '../../../../share/util/api/errorAction';
import { executeApi, handleResponseType, handleResult } from './Requirement065Api';

/** 各フェーズ定義 */
export const PHASE = {
  START: 'start',
  READY_OFF: 'readyOff',  //Ready OFF指示表示
  SWITCH_TO_FF: 'switchToFF',  //FF切り替え確認表示
  RETURN_TO_AWD: 'returnToAWD',  //AWD戻し確認表示
  SWITCH_TO_AWD: 'switchToAWD',  //AWD切り替え確認表示
  P_RANGE_SELECT: 'pRangeSelect',  //Pレンジ指示表示
  WRITING: 'writing',  //駆動モード判定
  SUCCESS_SWITCH_TO_AWD: 'successSwitchToAWD',  //AWD切り替え成功表示
  SUCCESS_SWITCH_TO_FF: 'successSwitchToFF',  //FF切り替え成功表示
  READY_OFF_FAILED: 'readyOffFailed',  //Ready OFF指示表示（2回目）
  FAILURE: 'failure',  //失敗表示
  FAILED: 'failed' //共通エラー
};

export default {
  name: 'requirement065',
  data() {
    return {
      phase: PHASE.START,
      isLoading: false,
      systemId: '',
      numWaitTime: 0,
      modal_requirement_065: false,
    };
  },

  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.READY_OFF_FAILED:
        case PHASE.FAILURE:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS_SWITCH_TO_AWD:
        case PHASE.SUCCESS_SWITCH_TO_FF:
          return 'success';
        case PHASE.START:
        case PHASE.READY_OFF:
        case PHASE.SWITCH_TO_FF:
        case PHASE.RETURN_TO_AWD:
        case PHASE.SWITCH_TO_AWD:
        case PHASE.P_RANGE_SELECT:
        case PHASE.WRITING:
        default:
          return 'secondary';
      }
    },

    /** ヘッダタイトル */
    headerTitle() {
      // /Ready OFF指示表示、FF切り替え確認表示、AWD戻し確認表示、AWD切り替え確認表示、Pレンジ指示表示
      const ConfirmationTitle = 'message.common_title_confirmation';
      // 駆動モード判定
      const ExecutionTitle = 'individual.common_title_execution';
      // AWD切り替え成功表示、FF切り替え成功表示
      const SuccessTitle = 'message.common_title_success';
      // Ready OFF指示表示（2回目）、失敗表示、共通エラー
      const ErrorTitle = 'message.common_title_error';
      const titleMap = {
        [PHASE.READY_OFF]: ConfirmationTitle,
        [PHASE.SWITCH_TO_FF]: ConfirmationTitle,
        [PHASE.RETURN_TO_AWD]: ConfirmationTitle,
        [PHASE.SWITCH_TO_AWD]: ConfirmationTitle,
        [PHASE.P_RANGE_SELECT]: ConfirmationTitle,
        [PHASE.START]: ExecutionTitle,
        [PHASE.WRITING]: ExecutionTitle,
        [PHASE.SUCCESS_SWITCH_TO_AWD]: SuccessTitle,
        [PHASE.SUCCESS_SWITCH_TO_FF]: SuccessTitle,
        [PHASE.READY_OFF_FAILED]: ErrorTitle,
        [PHASE.FAILURE]: ErrorTitle,
        [PHASE.FAILED]: ErrorTitle
      };
      return this.$t(titleMap[this.phase]);
    },

    /** キャンセルボタン表示 */
    showButtonCancel() {
      const showCancelButtonMap = {
        [PHASE.READY_OFF]: true,
        [PHASE.RETURN_TO_AWD]: true,
        [PHASE.P_RANGE_SELECT]: true,
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /** OKボタン表示 */
    showButtonOk() {
      const showCancelButtonMap = {
        [PHASE.READY_OFF]: true,
        [PHASE.RETURN_TO_AWD]: true,
        [PHASE.P_RANGE_SELECT]: true,
        [PHASE.SUCCESS_SWITCH_TO_AWD]: true,
        [PHASE.SUCCESS_SWITCH_TO_FF]: true,
        [PHASE.READY_OFF_FAILED]: true,
        [PHASE.FAILURE]: true,
        [PHASE.FAILED]: true
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /** NO and YES ボタン表示 */
    showButtonNoYes() {
      const showCancelButtonMap = {
        [PHASE.SWITCH_TO_FF]: true,
        [PHASE.SWITCH_TO_AWD]: true
      };
      let result = showCancelButtonMap[this.phase];
      if (!result) {
        // 合致しない場合は false とする。
        result = false;
      }
      return result;
    },

    /** モーダルボディ部 表示文言 */
    workSupportMessage() {
      const messageMap = {
        [PHASE.READY_OFF]: `${this.i18nWsKey}.REQ_WS_65_TXT_1`,
        [PHASE.SWITCH_TO_FF]: `${this.i18nWsKey}.REQ_WS_65_TXT_2`,
        [PHASE.RETURN_TO_AWD]: `${this.i18nWsKey}.REQ_WS_65_TXT_3`,
        [PHASE.SWITCH_TO_AWD]: `${this.i18nWsKey}.REQ_WS_65_TXT_4`,
        [PHASE.P_RANGE_SELECT]: `${this.i18nWsKey}.REQ_WS_65_TXT_5`,
        [PHASE.WRITING]: `${this.i18nWsKey}.REQ_WS_65_TXT_6`,
        [PHASE.SUCCESS_SWITCH_TO_AWD]: `${this.i18nWsKey}.REQ_WS_65_TXT_7`,
        [PHASE.SUCCESS_SWITCH_TO_FF]: `${this.i18nWsKey}.REQ_WS_65_TXT_8`,
        [PHASE.READY_OFF_FAILED]: `${this.i18nWsKey}.REQ_WS_65_TXT_9`,
        [PHASE.FAILURE]: `${this.i18nWsKey}.REQ_WS_65_TXT_10`,
        [PHASE.FAILED]: 'individual.error_communication_error'
      };
      return this.$te(messageMap[this.phase]) ? this.$t(messageMap[this.phase]) : '';
    },
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.modal_requirement_065 = true;
    },

    /** Wizardを閉じる */
    closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_065 = false;
    },

    /**
    * API 通信で共通バリデーションでエラーが発生した際のコールバック
    * @param {object} result handler.validate で返ってきたバリデーション結果
    */
    validateErrorResult(result) {
      apiErrorDefaultAction(
        this,
        result,
        // ログアウト処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック（Vuex の Action を呼びだしている点に注意）
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック、result を使って、任意の処理をすることも可能
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** 共通エラー表示 */
    showUnexpectedError() {
      this.phase = PHASE.FAILED;
    },

    /** キャンセルボタン押下時処理 */
    async onCancelClicked() {
      switch (this.phase) {
        case PHASE.READY_OFF:
          await this.dispose();
          break;
        case PHASE.RETURN_TO_AWD:
        case PHASE.P_RANGE_SELECT:
          await this.exit(0);
          break;
        default:
          break;
      }
    },

    /** OKボタン押下時処理 */
    async onOkClicked() {
      switch (this.phase) {
        case PHASE.READY_OFF:
          await this.getEngineStatus();
          break;
        case PHASE.RETURN_TO_AWD:
        case PHASE.P_RANGE_SELECT:
          await this.getShiftStatus();
          break;
        case PHASE.SUCCESS_SWITCH_TO_AWD:
        case PHASE.SUCCESS_SWITCH_TO_FF:
        case PHASE.READY_OFF_FAILED:
        case PHASE.FAILURE:
          await this.exit(0);
          break;
        case PHASE.FAILED:
          await this.exit(1);
          break;
        default:
          break;
      }
    },

    /** YESボタン押下時処理 */
    async onYesClicked() {
      if (this.phase === PHASE.SWITCH_TO_FF) {
        this.showReturnAWDPage();
      } else {
        await this.getShiftStatus();
      }
    },

    /** ReadyOFF状態読み出し処理 */
    async getEngineStatus() {
      await executeApi(this, 'read-start',
        // status = 'completed'
        async (self, response) => {
          await handleResponseType(self, response,
            // res_type = 'ok': 切り替え完了フラグ初期化を実行
            async (self) => { await self.initSwitchCompleteFlg(); },
            // res_type = 'ng': Ready OFF指示表示画面へ遷移
            () => { self.phase = PHASE.READY_OFF; }
          );
        }
      );
    },

    /** Dispose処理 */
    async dispose() {
      // 実行のみ
      await executeApi(this, 'dispose', undefined, undefined, true);
      this.closeWizard();
    },

    /** 切り替え完了フラグ初期化 */
    async initSwitchCompleteFlg() {
      await executeApi(this, 'write_with_session_transition',
        // status = 'completed'： 実行モード確認処理
        async (self) => { await self.getExecuteMode(); }
      );
    },

    /** 実行モード確認処理 */
    async getExecuteMode() {
      await executeApi(this, 'read-execute_mode',
        // status = 'completed'
        async (self, response) => {
          await handleResult(self, response,
            // result = 'awd': FF切り替え確認表示へ遷移
            (self) => { self.phase = PHASE.SWITCH_TO_FF; },
            // result = 'ff': AWD切り替え確認表示へ遷移
            (self) => { self.phase = PHASE.SWITCH_TO_AWD; }
          );
        }
      );
    },

    /** FF切り替え確認表示画面 OKボタン押下処理 */
    showReturnAWDPage() {
      // AWD戻し確認表示画面へ遷移
      this.phase = PHASE.RETURN_TO_AWD;
    },

    /** シフト状態読み出し処理 */
    async getShiftStatus() {
      await executeApi(this, 'read-check_shift_status',
        // status = 'completed'： シフト状態を取得
        async (self, response) => {
          await handleResponseType(self, response,
            // res_type = 'ok': Ready状態確認（２回目）画面へ遷移
            async (self) => { await self.getEngineStatusSecond(); },
            // res_type = 'ng': Pレンジ指示表示画面へ遷移
            () => { self.phase = PHASE.P_RANGE_SELECT; }
          );
        }
      );
    },

    /** ReadyOFF状態読み出し処理（２回目） */
    async getEngineStatusSecond() {
      await executeApi(this, 'read-check_ready_status',
        async (self, response) => {
          // status = 'completed'
          await handleResponseType(self, response,
            // res_type = 'ok': 駆動モード判定画面へ遷移
            async (self) => {
              self.phase = PHASE.WRITING;
              await self.getDriveModeChk();
            },
            // res_type = 'ng': エンジン停止指示表示（2回目）画面へ遷移
            () => { self.phase = PHASE.READY_OFF_FAILED; }
          );
        }
      );
    },

    /** 駆動モード判定処理 */
    async getDriveModeChk() {
      await executeApi(this, 'read-driving_mode_judgment',
        // status = 'completed'
        async (self, response) => {
          await handleResult(self, response,
            // result = 'awd': FF切り替え処理を実行
            async (self) => { await self.writeDriveFF(); },
            // result = 'ff': AWD切り替え処理を実行
            async (self) => { await self.writeDriveAWD(); },
            // result = 'ng': 終了処理(エラー遷移あり)
            async (self) => { await self.exit(0); }
          );
        }
      );
    },

    /** FF切り替え処理 */
    async writeDriveFF() {
      await executeApi(this, 'write-ff_switching',
        // status = 'completed'： 指定時間を計測後、切り替え完了フラグ確認
        async (self, response) => { await self.getSwitchCompleteFlgProcess(response); }
      );
    },

    /** AWD切り替え処理 */
    async writeDriveAWD() {
      await executeApi(this, 'write-awd_switching',
        // status = 'completed'： 指定時間を計測後、切り替え完了フラグ確認
        async (self, response) => { await self.getSwitchCompleteFlgProcess(response); }
      );
    },

    /**
     * AWD/FF切り替え後共通処理
     * @param {object} response 切り替えAPIレスポンス
     */
    async getSwitchCompleteFlgProcess(response) {
      this.numWaitTime = response.data.user_data.wait_time;
      await util.sleep(this.numWaitTime);
      this.numWaitTime = 0;
      await this.getSwitchCompleteFlg();
    },

    /** 切り替え完了フラグ確認処理 */
    async getSwitchCompleteFlg() {
      await executeApi(this, 'read-check_switch_completion_flag',
        // status = 'completed'： 切り替え完了フラグを取得
        async (self, response) => {
          await handleResponseType(self, response,
            // res_type = 'ok': AWD/FF情報読出しを実行
            async (self) => { await self.getDriveSystem(); },
            // res_type = 'ng': 失敗表示画面へ遷移
            () => { self.phase = PHASE.FAILURE; }
          );
        }
      );
    },

    /** AWD/FF情報読出し処理 */
    async getDriveSystem() {
      await executeApi(this, 'read-awd_ff',
        // status = 'completed'： AWD/FF情報を取得
        async (self, response) => {
          await handleResult(self, response,
            // result = 'awd': AWD切り替え成功表示画面へ遷移
            (self) => { self.phase = PHASE.SUCCESS_SWITCH_TO_AWD; },
            // result = 'ff': FF切り替え成功表示画面へ遷移
            (self) => { self.phase = PHASE.SUCCESS_SWITCH_TO_FF; }
          );
        }
      );
    },

    /** 
     * 終了処理
     * @param {number} intError 0以外の場合エラー遷移なし
     */
    async exit(intError) {
      await executeApi(this, 'exit',
        // status = 'completed'： 機能終了
        async (self) => { self.closeWizard(); },
        undefined, false, intError
      );
    },

  },

  async mounted() {
    this.init();
    // エンジン状態確認 読み出し処理
    await this.getEngineStatus();
  },
};